import React, { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import SettingsIcon from '@mui/icons-material/Settings';
import CampaignIcon from '@mui/icons-material/Campaign';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../theme/ThemeProvider';
import { SidebarNavigation, NavigationGroup } from '../../super-picker/components/sidebar';
import AppHeader from '../../super-picker/components/header/AppHeader';
import { useMobileMask } from '../context/MobileMaskContext';
import { SetupCheck } from '../services/api';
import { BOTTOM_BANNER_HEIGHT_VAR } from '../hooks/useReportBottomBannerHeight';

export type ActiveKey = 'dashboard' | 'clients' | 'pipeline' | 'why' | 'campaigns' | 'media' | 'settings' | 'admin';
export type ViewMode = 'admin' | 'non-admin' | 'demo';

const RED = '#ef4444';
const GREEN = '#22c55e';
/** Fallback before ResizeObserver reports the live banner height. */
const BOTTOM_BANNER_PADDING_FALLBACK = '140px';
const BOTTOM_BANNER_PADDING = `max(${BOTTOM_BANNER_PADDING_FALLBACK}, calc(var(${BOTTOM_BANNER_HEIGHT_VAR}, ${BOTTOM_BANNER_PADDING_FALLBACK}) + 8px))`;

interface Props {
  children: ReactNode;
  activeKey: ActiveKey;
  loginUser?: { Name?: string; Photo?: string } | null;
  isAdmin?: boolean;
  /** Granted non-admin agents may toggle into the Demo preview (but not Admin Mode). */
  canPreviewDemo?: boolean;
  /** Logged-in user with no Super Connector access. Demo is sample data; Live
   *  Mode is their own data. Configuration is a premium lock. Sidebar headers
   *  name Free vs Premium. */
  premiumPreview?: boolean;
  /** Current client view mode (admins can switch; includes a Demo preview). */
  viewMode?: ViewMode;
  /** Switch client view mode. Wired up for admins and demo-capable agents. */
  onViewModeChange?: (next: ViewMode) => void;
  /** Whether the app is showing the demo pitch (Why / Getting Started). */
  demo?: boolean;
  /** When a fixed bottom banner is visible, pad the main scroll area. */
  bottomBannerOffset?: boolean;
  /** Setup checklist rows for the navbar status button (null = not loaded yet). */
  setupChecks?: SetupCheck[] | null;
  /** Open the setup checklist dialog. */
  onOpenSetupChecklist?: () => void;
}

export default function SuperConnectorLayout({
  children,
  activeKey,
  loginUser,
  isAdmin,
  canPreviewDemo,
  premiumPreview = false,
  viewMode = 'non-admin',
  onViewModeChange,
  demo,
  bottomBannerOffset = false,
  setupChecks = null,
  onOpenSetupChecklist,
}: Props) {
  const navigate = useNavigate();
  const { currentTheme, setTheme } = useAppTheme();
  const { masked, loading: maskLoading, setMasked } = useMobileMask();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Whether the avatar opens the client-view-mode menu: admins get all modes,
  // granted agents get their live view + the Demo preview, and no-access
  // users can switch between Demo and Live Mode.
  const canOpenViewMenu = Boolean(isAdmin || canPreviewDemo || premiumPreview);
  // Avatar dropdown — switches client view mode.
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);
  const selectView = (next: ViewMode) => {
    onViewModeChange?.(next);
    setUserMenuAnchor(null);
  };

  const setupOutstanding = setupChecks ? setupChecks.filter((c) => !c.ok).length : 0;
  const showSetupButton = !demo && setupChecks != null && !!onOpenSetupChecklist;

  const navigationGroups: NavigationGroup[] = useMemo(() => [
    // Getting Started: the interactive "Why Super Connector" sign-up pitch,
    // shown only in the demo experience as the entry point.
    ...(demo
      ? [{
          id: 'getting-started',
          label: 'Getting Started',
          items: [
            {
              id: 'why',
              label: 'Why Super Connector',
              icon: <RocketLaunchIcon fontSize="small" />,
              active: activeKey === 'why',
              onClick: () => navigate('/super-connector/why'),
              tag: { label: 'Start Here' },
            },
          ],
        }]
      : []),
    {
      id: 'main',
      label: premiumPreview && !demo ? 'Main (Free)' : 'Main',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: <DashboardIcon fontSize="small" />,
          active: activeKey === 'dashboard',
          onClick: () => navigate('/super-connector/dashboard'),
        },
        {
          id: 'clients',
          label: 'Clients',
          icon: <PeopleIcon fontSize="small" />,
          active: activeKey === 'clients',
          onClick: () => navigate('/super-connector/clients'),
        },
        {
          id: 'pipeline',
          label: 'Pipeline',
          icon: <ViewKanbanIcon fontSize="small" />,
          active: activeKey === 'pipeline',
          onClick: () => navigate('/super-connector/pipeline'),
        },
      ],
    },
    // Configuration: Campaigns + Settings are browsable in demo mode (read-only
    // sample data); Admin stays hidden in demo and for non-admins.
    {
      id: 'config',
      label: premiumPreview && !demo ? 'Configuration (Premium)' : 'Configuration',
      items: [
        {
          id: 'campaigns',
          label: 'Campaigns',
          icon: <CampaignIcon fontSize="small" />,
          active: activeKey === 'campaigns',
          onClick: () => navigate('/super-connector/campaigns'),
        },
        {
          id: 'media',
          label: 'Media',
          icon: <PermMediaIcon fontSize="small" />,
          active: activeKey === 'media',
          onClick: () => navigate('/super-connector/media'),
        },
        {
          id: 'settings',
          label: 'Settings',
          icon: <SettingsIcon fontSize="small" />,
          active: activeKey === 'settings',
          onClick: () => navigate('/super-connector/settings'),
        },
        ...(isAdmin && !demo && !premiumPreview
          ? [{
              id: 'admin',
              label: 'Admin',
              icon: <AdminPanelSettingsIcon fontSize="small" />,
              active: activeKey === 'admin',
              onClick: () => navigate('/super-connector/admin'),
            }]
          : []),
      ],
    },
  ], [activeKey, navigate, isAdmin, demo, premiumPreview]);

  const setupButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 700,
    flexShrink: 0,
    borderColor: setupOutstanding > 0 ? RED : GREEN,
    color: setupOutstanding > 0 ? RED : GREEN,
    bgcolor: setupOutstanding > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
    '&:hover': {
      borderColor: setupOutstanding > 0 ? RED : GREEN,
      bgcolor: setupOutstanding > 0 ? 'rgba(239, 68, 68, 0.18)' : 'rgba(34, 197, 94, 0.18)',
    },
  };

  const setupButtonLabel = setupOutstanding > 0
    ? `Setup (${setupOutstanding})`
    : 'Setup complete';

  const setupButtonIcon = setupOutstanding > 0
    ? <WarningAmberIcon sx={{ fontSize: 18 }} />
    : <CheckCircleIcon sx={{ fontSize: 18 }} />;

  // Desktop keeps Setup in the navbar; mobile moves it to a bar under the
  // header so Huttons / theme / avatar aren't crushed.
  const setupHeaderAction = showSetupButton ? (
    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <Button
        size="small"
        variant="outlined"
        onClick={onOpenSetupChecklist}
        startIcon={setupButtonIcon}
        sx={setupButtonSx}
      >
        {setupOutstanding > 0 ? `Setup (${setupOutstanding})` : 'Setup'}
      </Button>
    </Box>
  ) : null;

  const headerHeight = 64;
  const setupMobileBarHeight = 44;
  const bodyTop = {
    xs: showSetupButton ? `${headerHeight + setupMobileBarHeight}px` : `${headerHeight}px`,
    md: `${headerHeight}px`,
  };
  const bodyHeight = {
    xs: showSetupButton
      ? `calc(100vh - ${headerHeight + setupMobileBarHeight}px)`
      : `calc(100vh - ${headerHeight}px)`,
    md: `calc(100vh - ${headerHeight}px)`,
  };

  return (
    <Box sx={{ height: '100vh', bgcolor: 'var(--color-bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppHeader
        currentTheme={currentTheme}
        userInfo={loginUser ?? null}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={() => setSidebarOpen((open) => !open)}
        onThemeToggle={() => setTheme(currentTheme === 'dark' ? 'light' : 'dark')}
        appLabel="SUPER Connector"
        actions={setupHeaderAction}
        onUserClick={(event) => setUserMenuAnchor(event.currentTarget)}
        userMenuOpen={userMenuOpen}
        showUserMenuIndicator
      />

      {showSetupButton && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            top: headerHeight,
            left: 0,
            right: 0,
            height: setupMobileBarHeight,
            zIndex: (t) => t.zIndex.appBar - 1,
            alignItems: 'center',
            px: 1.5,
            bgcolor: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
            boxShadow: '0 2px 4px rgba(var(--color-shadow-rgb), 0.15)',
          }}
        >
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={onOpenSetupChecklist}
            startIcon={setupButtonIcon}
            sx={{ ...setupButtonSx, py: 0.5 }}
          >
            {setupButtonLabel}
          </Button>
        </Box>
      )}

      <Box>
          <Menu
            anchorEl={userMenuAnchor}
            open={userMenuOpen}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 248, mt: 0.5 } } }}
          >
            {/* Client view mode is admin / demo-preview only; the privacy toggle
                below is for everyone, which is why the menu itself always opens. */}
            {canOpenViewMenu && (
              <>
              <Typography
                variant="caption"
                sx={{ display: 'block', px: 2, pt: 1, pb: 0.5, color: 'var(--color-text-faint)', fontWeight: 700, letterSpacing: 0.5 }}
              >
                CLIENT VIEW MODE
              </Typography>
              {isAdmin && (
                <MenuItem selected={viewMode === 'admin'} onClick={() => selectView('admin')}>
                  <ListItemIcon sx={{ color: viewMode === 'admin' ? '#ed8936' : 'inherit' }}>
                    {viewMode === 'admin' ? <CheckIcon fontSize="small" /> : <AdminPanelSettingsIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary="Admin Mode" primaryTypographyProps={{ fontWeight: 600 }} />
                </MenuItem>
              )}
              <MenuItem selected={viewMode === 'non-admin'} onClick={() => selectView('non-admin')}>
                <ListItemIcon sx={{ color: viewMode === 'non-admin' ? '#ed8936' : 'inherit' }}>
                  {viewMode === 'non-admin' ? <CheckIcon fontSize="small" /> : <PersonOutlineIcon fontSize="small" />}
                </ListItemIcon>
                {/* Only an admin sees this as the counterpart to Admin Mode; for a
                    non-admin there is nothing to contrast it with, so it reads as
                    "Live Mode" — their real data, as opposed to Demo Mode. */}
                <ListItemText
                  primary={isAdmin ? 'Non Admin Mode' : 'Live Mode'}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </MenuItem>
              <MenuItem selected={viewMode === 'demo'} onClick={() => selectView('demo')}>
                <ListItemIcon sx={{ color: viewMode === 'demo' ? '#ed8936' : 'inherit' }}>
                  {viewMode === 'demo' ? <CheckIcon fontSize="small" /> : <PersonOutlineIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText primary="Demo Mode" primaryTypographyProps={{ fontWeight: 600 }} secondary="Preview sample data" />
              </MenuItem>
              <Divider />
              </>
            )}

            <Typography
              variant="caption"
              sx={{ display: 'block', px: 2, pt: 1, pb: 0.5, color: 'var(--color-text-faint)', fontWeight: 700, letterSpacing: 0.5 }}
            >
              PRIVACY
            </Typography>
            {/* Masks the last 4 digits of client mobiles everywhere. Kept open
                (not closing the menu) so the effect is visible while toggling;
                the choice is saved per agent and restored on next sign-in. */}
            <MenuItem onClick={() => setMasked(!masked)} disabled={maskLoading}>
              <ListItemIcon sx={{ color: masked ? '#ed8936' : 'inherit' }}>
                {masked ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary="Mask Mobile Numbers"
                primaryTypographyProps={{ fontWeight: 600 }}
                secondary={masked ? 'Last 4 digits hidden' : 'Full numbers shown'}
              />
              <Switch edge="end" size="small" checked={masked} disabled={maskLoading} />
            </MenuItem>

            <Divider />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                Signed in as <strong>{loginUser?.Name || 'User'}</strong>
              </Typography>
            </Box>
          </Menu>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flex: 1, mt: bodyTop, height: bodyHeight, overflow: 'hidden' }}>
        <SidebarNavigation
          groups={navigationGroups}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          showMobileToggle={false}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, md: 3 },
            pb: bottomBannerOffset
              ? { xs: BOTTOM_BANNER_PADDING, md: BOTTOM_BANNER_PADDING }
              : { xs: 1.5, md: 3 },
            bgcolor: 'var(--color-bg)',
            minWidth: 0,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            color: 'var(--color-text-strong)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
